import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import './home.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faEye, faCalendarDay, faLock, faHourglassHalf, faDoorOpen, faGavel, faClock, faLandmark, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useHome } from '../../context/homeContext';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import PlusCircle from '../../assets/icons/pluscircle.svg';
import ReactPaginate from 'react-paginate';
import { useUsers } from '../../context/usersContext';
import VisibleAccess from '../../permissions/VisibleAccess';
import Skeleton from '../../components/Skeleton';
import { obterStatusVisualInterno } from '../../utils/internalStatus';

function Home() {
  const navigate = useNavigate();

  const {
    listHomeEntries,
    listHomeTransfers,
    listHomeSchedulings,
    listHomeQuantitativeCustody,
    listHomeQuantitativeOperational,
    listHomeTemporaryLeavings,
    listHomeDefinitiveLeavings,
  } = useHome();
  const { userInfo, userUnits, buildUnitReadParams, effectiveUnitId } = useUsers();

  const [homeData, setHomeData] = useState({ entries: [], transfer: [], transfers_sender: [], schedulings: [], temporary_leavings: [], definitive_leavings: [], quantitative: {} });
  const [isEntriesLoading, setIsEntriesLoading] = useState(true);
  const [isTransfersLoading, setIsTransfersLoading] = useState(true);
  const [isSchedulingsLoading, setIsSchedulingsLoading] = useState(true);
  const [isTemporaryLeavingsLoading, setIsTemporaryLeavingsLoading] = useState(true);
  const [isDefinitiveLeavingsLoading, setIsDefinitiveLeavingsLoading] = useState(true);
  const [isQuantitativeCustodyLoading, setIsQuantitativeCustodyLoading] = useState(true);
  const [isQuantitativeOperationalLoading, setIsQuantitativeOperationalLoading] = useState(true);
  const [currentCyclePage, setCurrentCyclePage] = useState(0);
  const [currentTransferPage, setCurrentTransferPage] = useState(0);
  const [currentTransferSenderPage, setCurrentTransferSenderPage] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [transferPerPage, setTransferPerPage] = useState(10);
  const [transferSenderPerPage, setTransferSenderPerPage] = useState(10);


  const scheduleRoutes = {
    "PRIMEIRO ATENDIMENTO SERVIÇO SOCIAL": { group: "SERVIÇO SOCIAL", route: "/servico-social" },
    "PRIMEIRO ATENDIMENTO PSICOLOGIA": { group: "PSICOLOGIA", route: "/psicologia" },
    "PRIMEIRO ATENDIMENTO ODONTOLOGIA": { group: "DENTISTA", route: "/odontologia" },
    "PRIMEIRO ATENDIMENTO SAUDE": { group: ["ENFERMAGEM", "MEDICINA"], route: "/admissao" },
    "PRIMEIRO ATENDIMENTO NUTRIÇÃO": { group: "NUTRIÇÃO", route: "/nutricao" },
  };

  const groupDefaultRoutes = {
    "SERVIÇO SOCIAL": "/servico-social",
    "PSICOLOGIA": "/psicologia",
    "DENTISTA": "/odontologia",
    "ENFERMAGEM": "/admissao",
    "MEDICINA": "/admissao",
    "NUTRIÇÃO": "/nutricao",
  };

  const getDefaultRouteByGroup = () => {
    const groups = userInfo?.groups || [];
    const match = groups.find((g) => groupDefaultRoutes[g]);
    return match ? groupDefaultRoutes[match] : null;
  };

  function AppointmentActions({ schedule, internalId }) {
    const config = scheduleRoutes[schedule.type_schedule];
    if (!config) return null; // não é um agendamento de "primeiro atendimento"

    const hasPermission = Array.isArray(config.group)
      ? userInfo?.groups?.some((group) => config.group.includes(group))
      : userInfo?.groups?.includes(config.group);
    if (!hasPermission) return null;

    return (
      <span className="seta-acao">⮕</span>
    );
  }

  function TransferAction() {
    const { userInfo } = useUsers();
    const navigate = useNavigate();

    const allowedGroups = ["ADMINISTRATIVO", "DISCIPLINA", "MONITORAMENTO"];
    const hasPermission = userInfo?.groups?.some(g => allowedGroups.includes(g));

    if (!hasPermission) return null;

    return (
      <span
        className="transfer-action-btn"
        onClick={() => navigate("/transferencia-unidade")}
        title="Ir para transferências"
      >
        <FontAwesomeIcon icon={faEye} />
      </span>
    );
  }

  function TransferFallbackAction({ internalId }) {
    const { userInfo } = useUsers();
    const allowedGroups = ["ADMINISTRATIVO", "DISCIPLINA", "MONITORAMENTO"];
    const hasPermission = userInfo?.groups?.some(g => allowedGroups.includes(g));
    if (hasPermission) return null;

    // Serviço Social vai direto para os detalhes do interno
    const isSocialService = userInfo?.groups?.includes("SERVIÇO SOCIAL");
    const fallbackRoute = isSocialService ? `/internos/${internalId}` : getDefaultRouteByGroup();
    if (!fallbackRoute) return null;

    return (
      <button
        className="btn-agendamento"
        onClick={() => (isSocialService ? navigate(fallbackRoute) : navigate(fallbackRoute, { state: { internalId } }))}
      >
        <FontAwesomeIcon icon={faEye} />
      </button>
    );
  }

  const normalizeDateStr = (s) => (typeof s === 'string' ? s.replace(' ', 'T') : '');
  const formatDateBR = (s) => {
    if (!s) return '';
    const d = new Date(normalizeDateStr(s));
    return isNaN(d) ? '' : d.toLocaleDateString('pt-BR');
  };

  const getScheduleResponsibleLabel = (typeSchedule) => {
    if (typeof typeSchedule !== 'string') return '';
    const normalized = typeSchedule.replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    const prefix = 'PRIMEIRO ATENDIMENTO ';
    if (normalized.toUpperCase().startsWith(prefix)) {
      return normalized.slice(prefix.length).trim();
    }
    return normalized;
  };

  useEffect(() => {
    const params = buildUnitReadParams({});
    // Cada bloco carrega e renderiza de forma independente, assim que a sua resposta chega.
    // O AbortController cancela requests em voo se a unidade mudar ou a tela desmontar
    // (evita request duplicado do StrictMode em dev e corrida de dados de unidade antiga).
    const controller = new AbortController();
    const opts = { signal: controller.signal };
    const stopLoading = (setter) => () => { if (!controller.signal.aborted) setter(false); };
    const ignoreAbort = () => {};

    setIsEntriesLoading(true);
    listHomeEntries(params, opts)
      .then((list) => setHomeData((prev) => ({ ...prev, entries: Array.isArray(list) ? list : [] })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsEntriesLoading));

    setIsTransfersLoading(true);
    listHomeTransfers(params, opts)
      .then((data) => setHomeData((prev) => ({
        ...prev,
        transfer: Array.isArray(data?.recipient) ? data.recipient : [],
        transfers_sender: Array.isArray(data?.sender) ? data.sender : [],
      })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsTransfersLoading));

    setIsSchedulingsLoading(true);
    listHomeSchedulings(params, opts)
      .then((list) => setHomeData((prev) => ({ ...prev, schedulings: Array.isArray(list) ? list : [] })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsSchedulingsLoading));

    setIsTemporaryLeavingsLoading(true);
    listHomeTemporaryLeavings(params, opts)
      .then((list) => setHomeData((prev) => ({ ...prev, temporary_leavings: Array.isArray(list) ? list : [] })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsTemporaryLeavingsLoading));

    setIsDefinitiveLeavingsLoading(true);
    listHomeDefinitiveLeavings(params, opts)
      .then((list) => setHomeData((prev) => ({ ...prev, definitive_leavings: Array.isArray(list) ? list : [] })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsDefinitiveLeavingsLoading));

    // Quantitativo dividido em dois endpoints: cada um mescla no card assim que responde.
    setIsQuantitativeCustodyLoading(true);
    listHomeQuantitativeCustody(params, opts)
      .then((data) => setHomeData((prev) => ({
        ...prev,
        quantitative: { ...prev.quantitative, ...(data && typeof data === 'object' ? data : {}) },
      })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsQuantitativeCustodyLoading));

    setIsQuantitativeOperationalLoading(true);
    listHomeQuantitativeOperational(params, opts)
      .then((data) => setHomeData((prev) => ({
        ...prev,
        quantitative: { ...prev.quantitative, ...(data && typeof data === 'object' ? data : {}) },
      })))
      .catch(ignoreAbort)
      .finally(stopLoading(setIsQuantitativeOperationalLoading));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveUnitId,
    listHomeEntries,
    listHomeTransfers,
    listHomeSchedulings,
    listHomeQuantitativeCustody,
    listHomeQuantitativeOperational,
    listHomeTemporaryLeavings,
    listHomeDefinitiveLeavings,
  ]);

  // Valor de um card do quantitativo: skeleton enquanto o endpoint da origem nao respondeu.
  const renderCardValue = (value, isLoading) => (
    isLoading
      ? <Skeleton className="card-value-skeleton" />
      : <span className="value">{value ?? 0}</span>
  );

  const handleCyclePageClick = ({ selected }) => {
    setCurrentCyclePage(selected);
  };
  const handleTransferPageClick = ({ selected }) => {
    setCurrentTransferPage(selected);
  };
  const handleTransferSenderPageClick = ({ selected }) => {
    setCurrentTransferSenderPage(selected);
  };

  const entriesList = Array.isArray(homeData.entries) ? homeData.entries : [];
  const transferList = Array.isArray(homeData.transfer) ? homeData.transfer : [];
  const transferSenderList = Array.isArray(homeData.transfers_sender) ? homeData.transfers_sender : [];
  const schedulingsList = Array.isArray(homeData.schedulings) ? homeData.schedulings : [];
  const temporaryLeavingsList = Array.isArray(homeData.temporary_leavings) ? homeData.temporary_leavings : [];
  const definitiveLeavingsList = Array.isArray(homeData.definitive_leavings) ? homeData.definitive_leavings : [];

  const cyclePageCount = Math.ceil((entriesList.length || 0) / entriesPerPage);
  const transferPageCount = Math.ceil((transferList.length || 0) / transferPerPage);
  const transferSenderPageCount = Math.ceil((transferSenderList.length || 0) / transferSenderPerPage);

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(parseInt(event.target.value, 10));
    setCurrentCyclePage(0);
  };

  const handleTransferPerPageChange = (event) => {
    setTransferPerPage(parseInt(event.target.value, 10));
    setCurrentTransferPage(0);
  };

  const handleTransferSenderPerPageChange = (event) => {
    setTransferSenderPerPage(parseInt(event.target.value, 10));
    setCurrentTransferSenderPage(0);
  };

  const getStatusClassByEndDate = (endDate) => {
    if (!endDate) return "";

    const src = new Date(normalizeDateStr(endDate));
    if (isNaN(src)) return "";

    // Datas somente com Y/M/D no horário local (meia-noite)
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endMid = new Date(src.getFullYear(), src.getMonth(), src.getDate());

    const diffInDays = Math.round((endMid - todayMid) / (1000 * 60 * 60 * 24));

    if (diffInDays <= 0) {
      // expirado ou vence hoje
      return "status-vermelho";
    }
    if (diffInDays === 1) {
      // vence amanhã
      return "status-amarelo";
    }
    // futuro > amanhã
    return "status-verde";
  };

  const unidadeAtiva = (
    Array.isArray(userUnits)
      ? userUnits.find((unit) => String(unit?.id) === String(effectiveUnitId))
      : null
  ) || (Array.isArray(userUnits) && userUnits.length === 1 ? userUnits[0] : null);

  const siglaUnidadeAtiva = unidadeAtiva?.acronym || '';

  const obterClasseStatusInterno = (status) => {
    const normalized = String(status || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();

    if (normalized === 'MONITORADO') return 'blue-text';
    if (normalized === 'PRESO') return 'red-text';
    if (normalized === 'FUGITIVO') return 'orange-text';
    if (normalized === 'LIBERADO') return 'green-text';
    if (normalized === 'SAIDA TEMPORARIA') return 'purple-text';
    return 'red-text';
  };

  const obterStatusVisualTransferencia = (transfer) => (
    obterStatusVisualInterno({
      siglaUnidadeInterno: siglaUnidadeAtiva,
      statusBackend: transfer?.internal?.status,
    })
  );

  const getEntrySchedulings = (entry) => {
    const schedulings = Array.isArray(entry?.schedulings) ? entry.schedulings : [];
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return schedulings.filter((s) => {
      if (!s.end_date) return true;
      const d = new Date(normalizeDateStr(s.end_date));
      if (isNaN(d)) return true;
      const endMid = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      return endMid >= todayMid;
    });
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className='container'>
        <Titulo
          children={<h2>Atividades</h2>}
          children2={
            <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "DISCIPLINA", "PLANTONISTA", "MONITORAMENTO"]}>
              <button
                className="add-button"
                onClick={() => navigate("/internos")}
              >
                <img src={PlusCircle} alt="icone" />
                Cadastrar interno
              </button>
            </VisibleAccess>
          }
        />

        <VisibleAccess compareField="groups" compareValues={["ADMINISTRATIVO", "DISCIPLINA", "PLANTONISTA", "GERENTE DE UNIDADE", "DIRETORES", "DUAP", "MONITORAMENTO"]}>
          <div className="quantitative-cards">

              {/* Sentenciados → nature_of_prison=Condenatoria */}
              <div
                className="card card-sentenciado"
                onClick={() => navigate("/quantitativo", { state: { type: "nature_of_prison", filter: "Condenatoria", label: "Sentenciados" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faGavel} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.sentenciados, isQuantitativeCustodyLoading)}
                  <span className="label">Sentenciados</span>
                </div>
              </div>

              {/* Provisórios → nature_of_prison=Provisorio */}
              <div
                className="card card-provisorio"
                onClick={() => navigate("/quantitativo", { state: { type: "nature_of_prison", filter: "Preventiva", label: "Provisórios" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faHourglassHalf} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.provisorios, isQuantitativeCustodyLoading)}
                  <span className="label">Provisórios</span>
                </div>
              </div>

              {/* Temporários → nature_of_prison=Temporario */}
              <div
                className="card card-temporario"
                onClick={() => navigate("/quantitativo", { state: { type: "nature_of_prison", filter: "Temporaria", label: "Temporários" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faClock} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.temporarios, isQuantitativeCustodyLoading)}
                  <span className="label">Temporários</span>
                </div>
              </div>

              {/* Prisão civil → nature_of_prison=Prisão civil */}
              <div
                className="card card-civil"
                onClick={() => navigate("/quantitativo", { state: { type: "nature_of_prison", filter: "Prisão civil", label: "Prisão civil" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faScaleBalanced} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.civis, isQuantitativeCustodyLoading)}
                  <span className="label">Prisão civil</span>
                </div>
              </div>

              {/* Fugitivos → status=Fugitivo */}
              <div
                className="card card-fugitivo"
                onClick={() => navigate("/quantitativo", { state: { type: "status", filter: "Fugitivo", label: "Foragidos" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faLock} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.fugitivo, isQuantitativeOperationalLoading)}
                  <span className="label">Foragido</span>
                </div>
              </div>

              {/* Fechados → regime=Fechado */}
              <div
                className="card card-fechado"
                onClick={() => navigate("/quantitativo", { state: { type: "regime", filter: "Fechado", label: "Fechados" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faLock} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.fechado, isQuantitativeCustodyLoading)}
                  <span className="label">Fechados</span>
                </div>
              </div>

              {/* Semiabertos → regime=Semi-aberto */}
              <div
                className="card card-semiaberto"
                onClick={() => navigate("/quantitativo", { state: { type: "regime", filter: "Semi-aberto", label: "Semiabertos" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faDoorOpen} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.semiaberto, isQuantitativeCustodyLoading)}
                  <span className="label">Semiabertos</span>
                </div>
              </div>

              {/* Abertos → regime=Aberto */}
              {/* <div
                className="card card-aberto"
                onClick={() => navigate("/quantitativo", { state: { type: "regime", filter: "Aberto" } })}
              >
                <div className="icon"><FontAwesomeIcon icon={faCalendarDay} /></div>
                <div className="info">
                  <span className="value">{homeData.quantitative.aberto ?? 0}</span>
                  <span className="label">Abertos</span>
                </div>
              </div> */}

              {/* Dia → abre listagem de internos da unidade */}
              <div
                className="card card-dia"
                onClick={() => navigate("/internos/unidade")}
                style={{ cursor: 'pointer' }}
              >
                <div className="icon"><FontAwesomeIcon icon={faCalendarDay} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.dia, isQuantitativeCustodyLoading)}
                  <span className="label">Quantidade PPLs</span>
                </div>
              </div>

              {/* Vagas (não clicável) */}
              <div className="card card-vagas">
                <div className="icon"><FontAwesomeIcon icon={faLandmark} /></div>
                <div className="info">
                  {renderCardValue(homeData.quantitative.vagas, isQuantitativeOperationalLoading)}
                  <span className="label">Vagas</span>
                </div>
              </div>

            </div>

        </VisibleAccess>


        <div className='container-title-home'>
          <h3 className='h3-home'>Entradas recentes</h3>
        </div>
        <div className='content content-table table-home'>
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Data entrada</th>
                <th>Data final do atend.</th>
                <th>Pavilhão</th>
                <th>Cela</th>
                <th></th>
              </tr>
            </thead>
            {isEntriesLoading ? (
              <Skeleton type="table" rows={5} cols={6} prefix="entries" />
            ) : entriesList.length > 0 ? (
              <tbody>
                {[...entriesList]
                  .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                  .slice(currentCyclePage * entriesPerPage, (currentCyclePage + 1) * entriesPerPage)
                  .map((entry) => (
                    <tr key={entry.id}>
                      <td className='td-limited'>{entry.internal?.name}</td>
                      <td className='td-limited'>{formatDateBR(entry.start_date)}</td>
                      <td className='td-limited'>
                        {getEntrySchedulings(entry).length > 0 ? (
                          getEntrySchedulings(entry).map((s) => {
                            const scheduleLabel = getScheduleResponsibleLabel(s.type_schedule);
                            return (
                              <div
                                key={s.id}
                                className={`home-schedule-item ${getStatusClassByEndDate(s.end_date)}`}
                              >
                                {formatDateBR(s.end_date)}
                                {scheduleLabel ? ` - ${scheduleLabel}` : ''}
                              </div>
                            );
                          })
                        ) : (
                          <span>Sem pendência</span>
                        )}
                      </td>
                      <td className='td-limited'>{entry.pavilion?.name}</td>
                      <td className='td-limited'>{entry.cell?.name}</td>
                      <td className='td-limited'>
                        {/* Caso o perfil seja ADMINISTRATIVO, DISCIPLINA ou PLANTÃO */}
                        {userInfo?.groups?.some(g => ["ADMINISTRATIVO", "DISCIPLINA", "PLANTÃO", "PLANTONISTA", "MONITORAMENTO"].includes(g)) ? (
                          <button
                            className="btn-agendamento"
                            onClick={() => navigate(`/internos/${entry.internal?.id}`)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        ) : (
                          /*Caso o perfil seja da humanização */
                          getEntrySchedulings(entry).length > 0 ? (
                            getEntrySchedulings(entry).map(s => {
                              const config = scheduleRoutes[s.type_schedule];
                              return (
                                config && (
                                  <button
                                    key={s.id}
                                    className="btn-agendamento"
                                    onClick={() => navigate(config.route, { state: { internalId: entry.internal?.id } })}
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </button>
                                )
                              );
                            })
                          ) : (
                            <button
                              className="btn-agendamento"
                              onClick={() => {
                                const fallbackRoute = getDefaultRouteByGroup();
                                if (fallbackRoute) {
                                  navigate(fallbackRoute, { state: { internalId: entry.internal?.id } });
                                  return;
                                }
                                navigate(`/internos/${entry.internal?.id}`);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={6}><p>Nenhum ciclo encontrado</p></td>
                </tr>
              </tbody>
            )}
          </table>
          {!isEntriesLoading && cyclePageCount > 1 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={cyclePageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handleCyclePageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
              forcePage={currentCyclePage}
            />
          )}
          {!isEntriesLoading && (
            <div className="pagination-options">
              <div>
                <label htmlFor="entriesPerPage">Entradas por página:</label>
                <select
                  id="entriesPerPage"
                  value={entriesPerPage}
                  onChange={handleEntriesPerPageChange}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className='container-title-home'>
          <h3 className='h3-home'>Transferências recentes</h3>
        </div>
        <div className='content content-table table-home'>
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Data entrada</th>
                <th>Origem</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            {isTransfersLoading ? (
              <Skeleton type="table" rows={5} cols={5} prefix="transfer" />
            ) : transferList.length > 0 ? (
              <tbody>
                {transferList
                  .slice(currentTransferPage * transferPerPage, (currentTransferPage + 1) * transferPerPage)
                  .map((transfer) => {
                    const statusVisualTransferencia = obterStatusVisualTransferencia(transfer);
                    return (
                      <tr key={transfer.id}>
                        <td className='td-limited'>{transfer.internal?.name}</td>
                        <td className='td-limited'>{formatDateBR(transfer.date)}</td>
                        <td className='td-limited'>{transfer.sender?.name}</td>
                        <td>
                          {('is_active' in transfer || 'received' in transfer || 'no_receipt' in transfer) ? (
                            (transfer.is_active && !transfer.received) ? (
                              <span className="status-vermelho">Pendente</span>
                            ) : (!transfer.is_active && transfer.received) ? (
                              <span className="status-verde">Realizada</span>
                            ) : (!transfer.is_active && !transfer.received && transfer.no_receipt) ? (
                              <span className="status-amarelo">Recusada</span>
                            ) : (
                              <span className="status-azul">Cancelada</span>
                            )
                          ) : (
                            <span className={obterClasseStatusInterno(statusVisualTransferencia)}>
                              {statusVisualTransferencia || 'Preso'}
                            </span>
                          )}
                        </td>
                        <td>
                          <TransferAction />
                          <TransferFallbackAction internalId={transfer.internal?.id} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={5}><p>Nenhuma transferência encontrada</p></td>
                </tr>
              </tbody>
            )}
          </table>
          {!isTransfersLoading && transferPageCount > 1 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={transferPageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handleTransferPageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
              forcePage={currentTransferPage}
            />
          )}
          {!isTransfersLoading && (
            <div className="pagination-options">
              <div>
                <label htmlFor="transferPerPage">Transferências por página:</label>
                <select
                  id="transferPerPage"
                  value={transferPerPage}
                  onChange={handleTransferPerPageChange}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className='container-title-home'>
          <h3 className='h3-home'>Saídas por transferência</h3>
        </div>
        <div className='content content-table table-home'>
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Data</th>
                <th>Origem</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            {isTransfersLoading ? (
              <Skeleton type="table" rows={5} cols={5} prefix="transfer-sender" />
            ) : transferSenderList.length > 0 ? (
              <tbody>
                {transferSenderList
                  .slice(currentTransferSenderPage * transferSenderPerPage, (currentTransferSenderPage + 1) * transferSenderPerPage)
                  .map((transfer) => {
                    const statusVisual = obterStatusVisualTransferencia(transfer);
                    return (
                      <tr key={transfer.id}>
                        <td className='td-limited'>{transfer.internal?.name}</td>
                        <td className='td-limited'>{formatDateBR(transfer.date)}</td>
                        <td className='td-limited'>{transfer.sender?.name}</td>
                        <td>
                          <span className={obterClasseStatusInterno(statusVisual)}>
                            {transfer.internal?.status || statusVisual || 'Em Transferência'}
                          </span>
                        </td>
                        <td>
                          <TransferAction />
                          <TransferFallbackAction internalId={transfer.internal?.id} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={5}><p>Nenhuma saída por transferência encontrada</p></td>
                </tr>
              </tbody>
            )}
          </table>
          {!isTransfersLoading && transferSenderPageCount > 1 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={transferSenderPageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handleTransferSenderPageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
              forcePage={currentTransferSenderPage}
            />
          )}
          {!isTransfersLoading && (
            <div className="pagination-options">
              <div>
                <label htmlFor="transferSenderPerPage">Saídas por página:</label>
                <select
                  id="transferSenderPerPage"
                  value={transferSenderPerPage}
                  onChange={handleTransferSenderPerPageChange}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className='container-title-home'>
          <h3 className='h3-home'>Saídas recentes</h3>
        </div>
        <div className='box-container'>
          <div className='box-home'>
            <div className="miniBox">
              <h4 className="h4-leaving h4-leaving-temporary">
                Saídas Temporárias
                <FontAwesomeIcon icon={faDoorOpen} />
              </h4>
              {isTemporaryLeavingsLoading ? (
                <p className="leaving-empty">Carregando...</p>
              ) : temporaryLeavingsList.length > 0 ? (
                temporaryLeavingsList.map((item) => (
                  <div key={item.id} className="leaving-item">
                    <div className="leaving-top">
                      <span className="leaving-name">{item.internal?.name}</span>
                    </div>
                    <div className="leaving-bottom">
                      <div className="leaving-meta">
                        <span className="leaving-label">Saída</span>
                        <span className="leaving-date">{formatDateBR(item.leaving_date)}</span>
                      </div>
                      <div className="leaving-meta">
                        <span className="leaving-label">Tipo</span>
                        <span className="leaving-type">{item.type_leaving?.name || '-'}</span>
                      </div>
                      <button
                        className="btn-leaving-eye"
                        onClick={() => navigate(`/internos/${item.internal?.id}`)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="leaving-empty">Nenhuma saída temporária</p>
              )}
            </div>

            <div className="miniBox">
              <h4 className="h4-leaving h4-leaving-definitive">
                Saídas Definitivas
                <FontAwesomeIcon icon={faDoorOpen} />
              </h4>
              {isDefinitiveLeavingsLoading ? (
                <p className="leaving-empty">Carregando...</p>
              ) : definitiveLeavingsList.length > 0 ? (
                definitiveLeavingsList.map((item) => (
                  <div key={item.id} className="leaving-item">
                    <div className="leaving-top">
                      <span className="leaving-name">{item.internal?.name}</span>
                    </div>
                    <div className="leaving-bottom">
                      <div className="leaving-meta">
                        <span className="leaving-label">Saída</span>
                        <span className="leaving-date">{formatDateBR(item.leaving_date)}</span>
                      </div>
                      <div className="leaving-meta">
                        <span className="leaving-label">Tipo</span>
                        <span className="leaving-type">{item.type_leaving?.name || '-'}</span>
                      </div>
                      <button
                        className="btn-leaving-eye"
                        onClick={() => navigate(`/internos/${item.internal?.id}`)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="leaving-empty">Nenhuma saída definitiva</p>
              )}
            </div>
          </div>
        </div>

        <div className='box-container'>
          <div className='box-home'>

            <div className="miniBox appointment">
              <h4 className="h4-atendimento-dia">
                Agendamentos do Dia
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </h4>
              {isSchedulingsLoading ? (
                <Skeleton
                  type="stack"
                  lines={3}
                  text="Carregando agendamentos..."
                  className="home-loading-box"
                  prefix="schedulings"
                />
              ) : schedulingsList.length > 0 ? (
                schedulingsList.map(s => (
                  <div key={s.id} className="appointment-item">
                    <h5 className="internal-name">{s.internal?.name}</h5>
                    <ul className="internal-schedulings">
                      <li
                        onClick={() => {
                          const config = scheduleRoutes[s.type_schedule];
                          if (config) {
                            navigate(config.route, { state: { internalId: s.internal?.id } });
                          }
                        }}
                        className="clickable-schedule"
                      >
                        {s.type_schedule}
                        {s.start_date ? ` - ${formatDateBR(s.start_date)}` : ''}
                        <AppointmentActions schedule={s} internalId={s.internal?.id} />
                      </li>
                    </ul>
                  </div>
                ))
              ) : (
                <p>Nenhum agendamento para hoje</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default Home;
